'use client';

import { useTranslation } from 'next-i18next';
import { Button, Anchor } from '@components/common';
import { FloatingNavBar } from './FloatingNavBar';
import { FloatingNavBarOverflowMenu } from './FloatingNavBarOverflowMenu';

export type CompanyNavButton = 'new-job' | 'coaching' | 'services';

const SELECTED_CLASS = '!border-ink !bg-primary/10 !text-ink shadow-none hover:!border-ink hover:!bg-primary/10 hover:!text-ink focus:!ring-0';
const DEFAULT_CLASS = '!border-transparent !bg-primary/10 !text-ink shadow-none hover:!border-transparent hover:!bg-primary/20 hover:!text-ink focus:!ring-0';
const BTN_CLASS = '!h-8 !px-2.5 text-xs !rounded-2xl lg:!h-11 lg:!px-8 lg:text-lg whitespace-nowrap';

/**
 * @description Company-specific floating nav bar shown on all company MC views.
 *
 * Buttons (left → right):
 * - **New Job** — navigates to the add-job flow (/mc/job-add/edit), auth-guarded.
 * - **Coaching** — selected on the MC hire landing page; clicking elsewhere returns to /mc.
 * - **Services** — redirects to /locale/mc/services.
 *
 * Pass `selectedButton` to mark the current page (adds border, disables click).
 * Pass `inline` to render as an in-flow element instead of a fixed overlay.
 */
export const CompanyFloatingNavBar = ({
  locale,
  selectedButton,
  pillBackground,
  inline,
  onNewJob,
  isAuthenticated = false,
}: {
  locale: Locale;
  selectedButton?: CompanyNavButton;
  pillBackground?: 'surface' | 'surface-100';
  inline?: boolean;
  /** Called when the New Job button is clicked. Caller handles auth redirect. */
  onNewJob?: () => void;
  isAuthenticated?: boolean;
}) => {
  const { t } = useTranslation('common');
  const newJobClass = `${BTN_CLASS} ${selectedButton === 'new-job' ? SELECTED_CLASS : DEFAULT_CLASS}`;

  const renderNewJobButton = () => {
    if (!isAuthenticated) {
      return (
        <Anchor
          href={`/login?_target_path=${encodeURIComponent(`/${locale}/mc/job-add/edit`)}`}
          styling="solid"
          color="light"
          className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
        >
          {t('homepage.react.button.addjob')}
        </Anchor>
      );
    }

    if (onNewJob) {
      return (
        <Button
          type="button"
          styling="solid"
          color="light"
          className={newJobClass}
          onClick={selectedButton === 'new-job' ? undefined : onNewJob}
        >
          {t('homepage.react.button.addjob')}
        </Button>
      );
    }

    return (
      <Anchor
        href={`/${locale}/mc/job-add/edit`}
        styling="solid"
        color="light"
        className={newJobClass}
      >
        {t('homepage.react.button.addjob')}
      </Anchor>
    );
  };

  return (
    <FloatingNavBar pillBackground={pillBackground} inline={inline}>
      <FloatingNavBarOverflowMenu isVisible disableOverflow>

        {/* New Job */}
        {renderNewJobButton()}

        {/* Coaching — selected on MC hire landing; links to /mc on other pages */}
        {selectedButton === 'coaching' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('mc.career.support')}
          </Button>
        ) : (
          <Anchor
            href={`/${locale}/mc`}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('mc.career.support')}
          </Anchor>
        )}

        {/* Services */}
        {selectedButton === 'services' ? (
          <Button
            type="button"
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${SELECTED_CLASS}`}
          >
            {t('menu.pricing')}
          </Button>
        ) : (
          <Anchor
            href={`/${locale}/mc/services`}
            styling="solid"
            color="light"
            className={`${BTN_CLASS} ${DEFAULT_CLASS}`}
          >
            {t('menu.pricing')}
          </Anchor>
        )}

      </FloatingNavBarOverflowMenu>
    </FloatingNavBar>
  );
};
